import { Controller } from "@hotwired/stimulus"
import {
  CASE_CARD_MODAL_BADGE_CLASS,
  CASE_CARD_MODAL_BADGE_LIST_CLASS,
  CASE_CARD_MODAL_BADGE_LIST_ITEM_CLASS,
  CASE_CARD_MODAL_LIST_BULLET_CLASS,
  CASE_CARD_MODAL_LIST_CLASS,
  CASE_CARD_MODAL_LIST_ITEM_CLASS,
  CASE_CARD_MODAL_LIST_TEXT_CLASS,
  CASE_CARD_MODAL_PARAGRAPH_CLASS,
  CASE_CARD_MODAL_SECTION_CLASS,
  CASE_CARD_MODAL_SECTION_TITLE_CLASS
} from "./case_card_modal_classes"

const ILLNESS_SCRIPT_KIND = "illness_script"

const SYNOPSIS_BADGE_TONES = {
  finalDiagnosis: "badge-aq-chip badge-aq-chip--pill-orange",
  clinicalExcellence: "badge-aq-chip bg-[var(--badge-aq-clinical-high-base-fill)]",
  basicScienceDisciplines: "badge-aq-chip badge-aq-chip--pill-dark-blue"
}

export default class extends Controller {
  static targets = ["subtitle", "body", "modal"]

  open(event) {
    event.preventDefault()

    const params = event.params
    this.subtitleTarget.textContent = params.title || ""
    this.bodyTarget.innerHTML = this.renderBody({
      body: params.body || "",
      kind: params.kind || "",
      finalDiagnoses: this.parseArrayParam(params.finalDiagnoses),
      clinicalExcellence: this.parseArrayParam(params.clinicalExcellence),
      basicScienceDisciplines: this.parseArrayParam(params.basicScienceDisciplines),
      clinicalFeatures: this.parseArrayParam(params.clinicalFeatures),
      coreConcepts: this.parseArrayParam(params.coreConcepts)
    })

    this.syncDialogDescription()
    this.modalController.show(event)
  }

  get modalController() {
    return this.application.getControllerForElementAndIdentifier(this.modalTarget, "railsui-modal")
  }

  parseArrayParam(value) {
    if (Array.isArray(value)) return value
    if (value == null || value === "") return []

    try {
      const parsed = JSON.parse(value)
      return Array.isArray(parsed) ? parsed : []
    } catch (_error) {
      return []
    }
  }

  renderBody({ body, kind, finalDiagnoses, clinicalExcellence, basicScienceDisciplines, clinicalFeatures, coreConcepts }) {
    const sections = []

    if (body) {
      sections.push(`<div class="${CASE_CARD_MODAL_PARAGRAPH_CLASS}">${body}</div>`)
    }

    if (kind === ILLNESS_SCRIPT_KIND) {
      if (clinicalFeatures.length > 0) {
        sections.push(this.renderListSection("Clinical features:", clinicalFeatures, "clinical-features"))
      }

      if (coreConcepts.length > 0) {
        sections.push(this.renderListSection("Core concepts:", coreConcepts, "core-concepts"))
      }
    } else {
      if (finalDiagnoses.length > 0) {
        sections.push(
          this.renderBadgeSection(
            "Final Diagnosis:",
            finalDiagnoses,
            "final-diagnosis",
            SYNOPSIS_BADGE_TONES.finalDiagnosis
          )
        )
      }

      if (clinicalExcellence.length > 0) {
        sections.push(
          this.renderBadgeSection(
            "Clinical Excellence Curricular Thread:",
            clinicalExcellence,
            "clinical-excellence",
            SYNOPSIS_BADGE_TONES.clinicalExcellence
          )
        )
      }

      if (basicScienceDisciplines.length > 0) {
        sections.push(
          this.renderBadgeSection(
            "Basic Science Curricular Thread:",
            basicScienceDisciplines,
            "basic-science-disciplines",
            SYNOPSIS_BADGE_TONES.basicScienceDisciplines
          )
        )
      }
    }

    return sections.join("")
  }

  renderListSection(title, items, sectionKey) {
    const titleId = this.sectionTitleId(sectionKey)
    const listItems = items
      .map((item) => {
        return `
          <li class="${CASE_CARD_MODAL_LIST_ITEM_CLASS}">
            <span class="${CASE_CARD_MODAL_LIST_BULLET_CLASS}" aria-hidden="true"></span>
            <span class="${CASE_CARD_MODAL_LIST_TEXT_CLASS}">${this.escapeHtml(item)}</span>
          </li>
        `
      })
      .join("")

    return `
      <section class="${CASE_CARD_MODAL_SECTION_CLASS}" aria-labelledby="${titleId}">
        <h3 id="${titleId}" class="${CASE_CARD_MODAL_SECTION_TITLE_CLASS}">${title}</h3>
        <ul class="${CASE_CARD_MODAL_LIST_CLASS}" role="list">${listItems}</ul>
      </section>
    `
  }

  renderBadgeSection(title, items, sectionKey, badgeClasses) {
    const titleId = this.sectionTitleId(sectionKey)
    const badges = items
      .map((item) => {
        return `
          <span class="${CASE_CARD_MODAL_BADGE_LIST_ITEM_CLASS}" role="listitem">
            <span class="${badgeClasses} ${CASE_CARD_MODAL_BADGE_CLASS}"><span>${this.escapeHtml(item)}</span></span>
          </span>
        `
      })
      .join("")

    return `
      <section class="${CASE_CARD_MODAL_SECTION_CLASS}" aria-labelledby="${titleId}">
        <h3 id="${titleId}" class="${CASE_CARD_MODAL_SECTION_TITLE_CLASS}">${title}</h3>
        <div class="${CASE_CARD_MODAL_BADGE_LIST_CLASS}" role="list" aria-labelledby="${titleId}">
          ${badges}
        </div>
      </section>
    `
  }

  sectionTitleId(sectionKey) {
    return `case-card-synopsis-${sectionKey}-title`
  }

  syncDialogDescription() {
    const container = this.modalTarget.querySelector("[data-railsui-modal-target='container']")
    if (!container) return

    const describedby = [this.subtitleTarget.id, this.bodyTarget.id].filter(Boolean).join(" ")
    if (describedby) {
      container.setAttribute("aria-describedby", describedby)
    }
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }
}
